<template>
  <div class="container flex-grow-1 d-flex flex-column">
    <Loading v-if="loading" />

    <div
      v-else-if="activities.length <= 0"
      class="d-flex flex-column align-items-center justify-content-center flex-grow-1"
    >
      <div class="text-center">
        <font-awesome-icon
          :icon="['far', 'clock-rotate-left']"
          size="3x"
          class="mb-3 text-secondary"
        />
        <p class="mb-5 text-secondary">
          {{ $t("activities.empty_message") }}
        </p>
      </div>
    </div>

    <div class="my-4" v-else>
      <div
        class="d-flex flex-column flex-lg-row gap-3 align-items-stretch align-items-lg-center justify-content-between mb-4"
      >
        <div class="input-group">
          <span class="input-group-text">
            <font-awesome-icon :icon="['far', 'magnifying-glass']" />
          </span>
          <input
            type="text"
            class="form-control"
            :placeholder="$t('activities.search_placeholder')"
            v-model="searchQuery"
          />
          <button
            v-if="searchQuery"
            class="btn btn-secondary"
            type="button"
            :aria-label="$t('activities.clear_search')"
            @click="searchQuery = ''"
          >
            <font-awesome-icon :icon="['far', 'xmark']" class="text-white" />
          </button>
        </div>

        <div class="d-flex align-items-center gap-2">
          <select
            class="form-select"
            v-model="filterActionType"
            style="max-width: 200px"
          >
            <option value="">{{ $t("activities.all_types") }}</option>
            <option value="user">{{ $t("activities.user_actions") }}</option>
            <option value="tool">{{ $t("activities.tool_actions") }}</option>
            <option value="auth">{{ $t("activities.auth_actions") }}</option>
          </select>
        </div>
      </div>

      <div
        v-if="hasNoFilteredResults"
        class="alert alert-secondary text-center"
        role="status"
      >
        {{ $t("activities.no_filtered_results") }}
      </div>

      <div class="table-responsive card" v-else>
        <table class="table table-hover mb-0">
          <thead>
            <tr>
              <th scope="col">{{ $t("activities.timestamp") }}</th>
              <th scope="col">{{ $t("activities.actor") }}</th>
              <th scope="col">{{ $t("activities.action") }}</th>
              <th scope="col">{{ $t("activities.details") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="activity in filteredActivities" :key="activity.id">
              <td class="text-nowrap">
                {{ formatTimestamp(activity.timestamp) }}
              </td>
              <td>
                <div class="d-flex flex-column">
                  <span class="fw-bold">{{ activity.actorDisplayName }}</span>
                  <small class="text-muted">{{ activity.actorEmail }}</small>
                </div>
              </td>
              <td>
                <span :class="getActionBadgeClass(activity.actionType)">
                  {{ $t(`activities.actions.${activity.actionType}`) }}
                </span>
              </td>
              <td>
                <div class="activity-details">
                  <div v-if="activity.targetUserName || activity.targetUser">
                    <strong>{{ $t("activities.target_user") }}:</strong>
                    {{ activity.targetUserName || activity.targetUser }}
                  </div>
                  <div v-if="activity.targetTool">
                    <strong>{{ $t("activities.target_tool") }}:</strong>
                    {{ activity.targetTool }}
                  </div>
                  <div
                    v-if="hasClaimsDelta(activity)"
                    class="mt-2"
                  >
                    <strong>{{ $t("activities.claims_delta") }}:</strong>

                    <div
                      v-if="activity.metadata.claimsDelta.added?.length"
                      class="mt-1"
                    >
                      <strong>{{ $t("activities.claims_added") }}:</strong>
                      <code class="ms-1">{{ formatObject(activity.metadata.claimsDelta.added) }}</code>
                    </div>

                    <div
                      v-if="activity.metadata.claimsDelta.removed?.length"
                      class="mt-1"
                    >
                      <strong>{{ $t("activities.claims_removed") }}:</strong>
                      <code class="ms-1">{{ formatObject(activity.metadata.claimsDelta.removed) }}</code>
                    </div>

                    <div
                      v-if="activity.metadata.claimsDelta.changed?.length"
                      class="mt-1"
                    >
                      <strong>{{ $t("activities.claims_changed") }}:</strong>
                      <code class="ms-1">{{ formatObject(activity.metadata.claimsDelta.changed) }}</code>
                    </div>
                  </div>
                  <div v-if="activity.claimKey" class="mt-1">
                    <strong>{{ $t('activities.claim_key') }}:</strong>
                    <code class="ms-1">{{ activity.claimKey }}</code>
                  </div>
                  <div v-if="activity.before" class="mt-1">
                    <strong>{{ $t("activities.before") }}:</strong>
                    <code class="ms-1">{{ formatObject(activity.before) }}</code>
                  </div>
                  <div v-if="activity.after" class="mt-1">
                    <strong>{{ $t("activities.after") }}:</strong>
                    <code class="ms-1">{{ formatObject(activity.after) }}</code>
                  </div>
                  <div
                    v-if="
                      activity.accessGranted !== undefined &&
                      activity.accessGranted !== null
                    "
                    class="mt-1"
                  >
                    <strong>{{ $t("activities.access") }}:</strong>
                    <span
                      :class="
                        activity.accessGranted
                          ? 'text-success'
                          : 'text-danger'
                      "
                    >
                      {{
                        activity.accessGranted
                          ? $t("activities.granted")
                          : $t("activities.revoked")
                      }}
                    </span>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <nav v-if="totalPages > 1" class="mt-4">
        <ul class="pagination justify-content-center">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a
              class="page-link"
              href="#"
              @click.prevent="currentPage > 1 && currentPage--"
            >
              {{ $t("activities.previous") }}
            </a>
          </li>
          <li
            v-for="page in visiblePages"
            :key="page"
            class="page-item"
            :class="{ active: currentPage === page }"
          >
            <a class="page-link" href="#" @click.prevent="currentPage = page">
              {{ page }}
            </a>
          </li>
          <li
            class="page-item"
            :class="{ disabled: currentPage === totalPages }"
          >
            <a
              class="page-link"
              href="#"
              @click.prevent="currentPage < totalPages && currentPage++"
            >
              {{ $t("activities.next") }}
            </a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { firestore } from "../plugins/firebase";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import Loading from "./LoadingComponent.vue";

export default {
  name: "ActivitiesView",
  components: {
    Loading,
  },
  setup() {
    const store = useStore();
    const user = computed(() => store.getters.user);

    const activities = ref([]);
    const loading = ref(true);
    const searchQuery = ref("");
    const filterActionType = ref("");
    const currentPage = ref(1);
    const itemsPerPage = 20;

    const loadActivities = async () => {
      loading.value = true;
      try {
        const activitiesRef = collection(firestore, "activities");
        const q = query(
          activitiesRef,
          orderBy("timestamp", "desc"),
          limit(500) // Limit für Performance
        );

        const querySnapshot = await getDocs(q);
        activities.value = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      } catch (error) {
        console.error("Fehler beim Laden der Aktivitäten:", error);
        store.commit("addError", "Aktivitäten konnten nicht geladen werden.");
      } finally {
        loading.value = false;
      }
    };

    const formatTimestamp = (timestamp) => {
      if (!timestamp) return "-";
      
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else {
        date = new Date(timestamp);
      }

      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Gerade eben";
      if (diffMins < 60) return `vor ${diffMins} Min`;
      if (diffHours < 24) return `vor ${diffHours} Std`;
      if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? "en" : ""}`;

      return date.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    const formatObject = (obj) => {
      if (typeof obj === "object" && obj !== null) {
        return JSON.stringify(obj, null, 2);
      }
      return String(obj);
    };

    const hasClaimsDelta = (activity) => {
      const delta = activity?.metadata?.claimsDelta;
      return !!(
        delta &&
        ((delta.added && delta.added.length) ||
          (delta.removed && delta.removed.length) ||
          (delta.changed && delta.changed.length))
      );
    };

    const getActionBadgeClass = (actionType) => {
      const baseClass = "badge";
      
      if (actionType.includes("created")) return `${baseClass} bg-success`;
      if (actionType.includes("deleted")) return `${baseClass} bg-danger`;
      if (actionType.includes("updated") || actionType.includes("changed"))
        return `${baseClass} bg-warning text-dark`;
      if (actionType.includes("login") || actionType.includes("logout"))
        return `${baseClass} bg-info text-dark`;
      
      return `${baseClass} bg-secondary`;
    };

    const getActionCategory = (actionType) => {
      if (actionType.startsWith("user_")) return "user";
      if (actionType.startsWith("tool_")) return "tool";
      if (actionType === "login" || actionType === "logout") return "auth";
      return "other";
    };

    const filteredActivities = computed(() => {
      let filtered = activities.value;

      // Filter nach Aktionstyp
      if (filterActionType.value) {
        filtered = filtered.filter(
          (activity) =>
            getActionCategory(activity.actionType) === filterActionType.value
        );
      }

      // Textsuche
      if (searchQuery.value) {
        const search = searchQuery.value.toLowerCase();
        filtered = filtered.filter((activity) => {
          return (
            activity.actorDisplayName?.toLowerCase().includes(search) ||
            activity.actorEmail?.toLowerCase().includes(search) ||
            activity.targetUserName?.toLowerCase().includes(search) ||
            activity.targetUser?.toLowerCase().includes(search) ||
            activity.targetTool?.toLowerCase().includes(search) ||
            activity.actionType?.toLowerCase().includes(search)
          );
        });
      }

      // Paginierung
      const start = (currentPage.value - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      return filtered.slice(start, end);
    });

    const hasNoFilteredResults = computed(() => {
      if (!searchQuery.value && !filterActionType.value) return false;
      return filteredActivities.value.length === 0;
    });

    const totalPages = computed(() => {
      let filtered = activities.value;

      if (filterActionType.value) {
        filtered = filtered.filter(
          (activity) =>
            getActionCategory(activity.actionType) === filterActionType.value
        );
      }

      if (searchQuery.value) {
        const search = searchQuery.value.toLowerCase();
        filtered = filtered.filter((activity) => {
          return (
            activity.actorDisplayName?.toLowerCase().includes(search) ||
            activity.actorEmail?.toLowerCase().includes(search) ||
            activity.targetUserName?.toLowerCase().includes(search) ||
            activity.targetUser?.toLowerCase().includes(search) ||
            activity.targetTool?.toLowerCase().includes(search) ||
            activity.actionType?.toLowerCase().includes(search)
          );
        });
      }

      return Math.ceil(filtered.length / itemsPerPage);
    });

    const visiblePages = computed(() => {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
      let end = Math.min(totalPages.value, start + maxVisible - 1);

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    });

    onMounted(() => {
      loadActivities();
    });

    return {
      user,
      activities,
      loading,
      searchQuery,
      filterActionType,
      currentPage,
      filteredActivities,
      hasNoFilteredResults,
      totalPages,
      visiblePages,
      formatTimestamp,
      formatObject,
      hasClaimsDelta,
      getActionBadgeClass,
    };
  },
};
</script>
